import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  useGetMyRefereeRequestsQuery,
  useAcceptRefereeRequestMutation,
  useRejectRefereeRequestMutation,
} from "../api/probationerApplicationApi";
import {
  UserCheck,
  Clock,
  CheckCircle2,
  XCircle,
  ArrowLeft,
  RefreshCw,
  ExternalLink,
  MessageSquare,
  AlertCircle,
  FileText,
} from "lucide-react";

function safeMsg(x: any) {
  return String(
    x?.data?.message || x?.message || x?.error || "Something went wrong",
  );
}

const TABS = [
  { key: "pending", label: "Pending", icon: Clock },
  { key: "approved", label: "Approved", icon: CheckCircle2 },
  { key: "rejected", label: "Rejected", icon: XCircle },
  { key: "all", label: "All Requests", icon: FileText },
] as const;

type TabKey = (typeof TABS)[number]["key"];

function normalizeList(res: any): any[] {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  return [];
}

function getDecision(r: any): "pending" | "approved" | "rejected" {
  const v = String(r?.decision ?? "pending").toLowerCase();
  if (v === "approved") return "approved";
  if (v === "rejected") return "rejected";
  return "pending";
}

function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { dateStyle: "medium" });
}

export default function MyRefereeRequestsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>("pending");
  const [remarksById, setRemarksById] = useState<Record<string, string>>({});
  const [localError, setLocalError] = useState<string | null>(null);
  const [localSuccess, setLocalSuccess] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);

  const {
    data: raw,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetMyRefereeRequestsQuery({ decision: tab });
  const [accept] = useAcceptRefereeRequestMutation();
  const [reject] = useRejectRefereeRequestMutation();

  const allRows = useMemo(() => normalizeList(raw), [raw]);
  const busy = isLoading || isFetching;

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0, all: allRows.length };
    for (const x of allRows) {
      const d = getDecision(x);
      if (d === "pending") c.pending++;
      if (d === "approved") c.approved++;
      if (d === "rejected") c.rejected++;
    }
    return c;
  }, [allRows]);

  const visible = useMemo(
    () =>
      tab === "all" ? allRows : allRows.filter((x) => getDecision(x) === tab),
    [allRows, tab],
  );

  const onAction = async (refereeId: string, type: "accept" | "reject") => {
    setLocalError(null);
    setLocalSuccess(null);

    const remarks = (remarksById[refereeId] || "").trim();

    if (type === "reject" && !remarks) {
      setLocalError("A reason is required for rejection.");
      return;
    }

    setActingId(refereeId);
    try {
      const action = type === "accept" ? accept : reject;

      // Change: Ensure remarks is a string, even if empty
      const res: any = await action({
        refereeId,
        remarks: remarks, // TypeScript is happy now because 'remarks' is a string
      }).unwrap();

      setLocalSuccess(res?.message || `Request ${type}ed successfully.`);
      refetch();
    } catch (e) {
      setLocalError(safeMsg(e));
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-widest">
            <Link
              to="/dashboard"
              className="hover:text-gray-900 transition-colors"
            >
              Dashboard
            </Link>
            <span className="opacity-30">/</span>
            <span className="text-gray-900">Referee Requests</span>
          </div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">
            Referee Requests
          </h1>
          <p className="text-sm text-gray-500 font-medium">
            Manage and review applications where you have been nominated as a
            referee.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            disabled={busy}
            className="p-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all shadow-sm disabled:opacity-50"
          >
            <RefreshCw
              className={`w-5 h-5 text-gray-600 ${busy ? "animate-spin" : ""}`}
            />
          </button>
        </div>
      </div>

      {/* Global Alerts */}
      {(localSuccess || localError) && (
        <div
          className={`p-4 rounded-2xl border flex items-center gap-3 animate-in slide-in-from-top-2 ${
            localSuccess
              ? "bg-green-50 border-green-100 text-green-800"
              : "bg-red-50 border-red-100 text-red-800"
          }`}
        >
          {localSuccess ? (
            <CheckCircle2 className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <p className="text-sm font-bold tracking-tight">
            {localSuccess || localError}
          </p>
        </div>
      )}

      {/* Modern Tabs */}
      <div className="bg-gray-100/50 p-1.5 rounded-[20px] flex flex-wrap gap-1">
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = tab === t.key;
          const count = counts[t.key as keyof typeof counts];
          return (
            <button
              key={t.key}
              onClick={() => {
                setTab(t.key);
                setLocalError(null);
                setLocalSuccess(null);
              }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl text-xs font-black uppercase tracking-widest transition-all ${
                isActive
                  ? "bg-white text-gray-900 shadow-sm ring-1 ring-black/5"
                  : "text-gray-400 hover:text-gray-600"
              }`}
            >
              <Icon
                className={`w-3.5 h-3.5 ${isActive ? "text-blue-500" : ""}`}
              />
              {t.label}
              <span
                className={`ml-1 px-1.5 py-0.5 rounded-md ${
                  isActive
                    ? "bg-gray-100 text-gray-900"
                    : "bg-gray-200/50 text-gray-400"
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Request List */}
      <div className="space-y-6">
        {isLoading ? (
          <div className="py-20 text-center animate-pulse">
            <p className="text-xs font-black text-gray-400 uppercase tracking-widest">
              Loading Requests...
            </p>
          </div>
        ) : visible.length === 0 ? (
          <div className="py-20 bg-white border-2 border-dashed border-gray-100 rounded-[40px] text-center">
            <div className="bg-gray-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <UserCheck className="w-8 h-8 text-gray-200" />
            </div>
            <p className="text-sm font-bold text-gray-400 uppercase tracking-widest">
              No {tab} requests found
            </p>
          </div>
        ) : (
          visible.map((r: any) => {
            const app = r.application;
            const decision = getDecision(r);
            const isPending = decision === "pending";
            const rowBusy = actingId === r.id;

            return (
              <div
                key={r.id}
                className="bg-white border border-gray-200 rounded-[32px] overflow-hidden shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="p-8">
                  <div className="flex flex-col md:flex-row justify-between gap-6">
                    {/* Left: Applicant Info */}
                    <div className="space-y-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-2 py-0.5 rounded-md text-[10px] font-black uppercase border ${
                              isPending
                                ? "bg-amber-50 text-amber-600 border-amber-100"
                                : decision === "approved"
                                ? "bg-green-50 text-green-600 border-green-100"
                                : "bg-red-50 text-red-600 border-red-100"
                            }`}
                          >
                            {decision}
                          </span>
                          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-tighter">
                            ID: {r.id}
                          </span>
                        </div>
                        <h3 className="text-xl font-black text-gray-900 capitalize">
                          {app?.surname} {app?.other_names}
                          <span className="text-gray-300 font-medium ml-2">
                            ({app?.title || "Applicant"})
                          </span>
                        </h3>
                        <p className="text-sm font-bold text-gray-500 flex items-center gap-2">
                          <MessageSquare className="w-4 h-4" />
                          Relationship:{" "}
                          <span className="text-gray-900">
                            {r.relationship || "Not specified"}
                          </span>
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-4 pt-2">
                        <div className="bg-gray-50 p-3 rounded-2xl">
                          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                            Application Diet
                          </p>
                          <p className="text-xs font-bold text-gray-800 truncate">
                            {app?.diet?.title || "—"}
                          </p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-2xl">
                          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                            Requested On
                          </p>
                          <p className="text-xs font-bold text-gray-800">
                            {fmtDate(r.created_at)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Right: View Button */}
                    <div className="flex md:flex-col items-end gap-2">
                      <Link
                        to={`/my-referee-request/${r.probationer_application_id}`}
                        className="flex items-center gap-2 px-5 py-3 bg-gray-900 text-white rounded-2xl text-xs font-bold hover:bg-gray-800 transition-all group"
                      >
                        Review Portfolio{" "}
                        <ExternalLink className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                      </Link>
                    </div>
                  </div>

                  {/* Remarks & Action Area */}
                  <div className="mt-8 pt-8 border-t border-gray-50">
                    {isPending ? (
                      <div className="grid md:grid-cols-12 gap-6 items-end">
                        <div className="md:col-span-8 space-y-2">
                          <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">
                            Your Professional Remarks
                          </label>
                          <textarea
                            className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl text-sm outline-none focus:ring-2 focus:ring-gray-900/5 focus:bg-white transition-all min-h-[80px]"
                            placeholder="Provide feedback on the candidate's character and competence..."
                            value={remarksById[r.id] || ""}
                            onChange={(e) =>
                              setRemarksById((p) => ({
                                ...p,
                                [r.id]: e.target.value,
                              }))
                            }
                            disabled={rowBusy}
                          />
                        </div>
                        <div className="md:col-span-4 flex gap-3">
                          <button
                            onClick={() => onAction(r.id, "reject")}
                            disabled={rowBusy}
                            className="flex-1 py-4 bg-white border border-red-100 text-red-600 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-red-50 transition-all disabled:opacity-50"
                          >
                            {rowBusy ? "..." : "Reject"}
                          </button>
                          <button
                            onClick={() => onAction(r.id, "accept")}
                            disabled={rowBusy}
                            className="flex-1 py-4 bg-green-600 text-white rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-green-700 shadow-lg shadow-green-100 transition-all disabled:opacity-50"
                          >
                            {rowBusy ? "..." : "Approve"}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gray-50 p-6 rounded-3xl border border-gray-100 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                            Decision finalized on{" "}
                            {fmtDate(r.approved_at || r.rejected_at)}
                          </span>
                          {decision === "approved" ? (
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-500" />
                          )}
                        </div>
                        <div className="flex gap-4">
                          <div className="mt-1">
                            <MessageSquare className="w-4 h-4 text-gray-300" />
                          </div>
                          <p className="text-sm text-gray-600 italic leading-relaxed">
                            "{r.remarks || "No remarks provided."}"
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
